import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";
import OpenShiftDialog from "./OpenShiftDialog";
import { useStore } from "../context/StoreContext";

/**
 * Mounted globally inside <Layout>. Two responsibilities:
 *
 * 1. After login (or store switch), check `/shifts/active`. If nothing is
 *    open, show a forced OpenShiftDialog so staff is reminded *immediately*
 *    that the till must be opened before any sale.
 *
 * 2. Listen on a global `axios` response interceptor for HTTP 409 errors
 *    whose detail starts with `NO_ACTIVE_SHIFT:` (raised by the backend
 *    create-sale endpoint). On that error, re-open the dialog and surface a
 *    toast so the cashier knows *exactly* why the sale didn't go through.
 *
 * The dialog can be skipped — the backend is the real gate, this is just
 * UX. After Skip, the next sale attempt will surface the dialog again.
 */
export default function ShiftGuard() {
  const { currentStore } = useStore();
  const [needShift, setNeedShift] = useState(false);
  const [checked, setChecked] = useState(false);
  const interceptorId = useRef(null);

  const check = useCallback(async () => {
    try {
      const r = await api.get("/shifts/active");
      setNeedShift(!r.data?.shift);
    } catch {
      // If the call fails (network), don't pester the user — the backend
      // gate will still block the sale and prompt later.
      setNeedShift(false);
    } finally {
      setChecked(true);
    }
  }, []);

  // 1. Initial check on mount + when the user switches store.
  useEffect(() => {
    setChecked(false);
    check();
  }, [currentStore?.id, check]);

  // 2. Hook the axios response interceptor. When a sale fails with
  // 409 NO_ACTIVE_SHIFT, force the popup back open.
  useEffect(() => {
    interceptorId.current = api.interceptors.response.use(
      (r) => r,
      (error) => {
        const status = error?.response?.status;
        const reason = error?.response?.headers?.["x-reason"] || "";
        if (status === 409 && reason === "NO_ACTIVE_SHIFT") {
          setNeedShift(true);
        }
        return Promise.reject(error);
      },
    );
    return () => {
      if (interceptorId.current != null) {
        api.interceptors.response.eject(interceptorId.current);
      }
    };
  }, []);

  if (!checked || !needShift) return null;
  return (
    <OpenShiftDialog
      forced
      storeName={currentStore?.name || ""}
      onClose={() => setNeedShift(false)}
      onOpened={() => setNeedShift(false)}
    />
  );
}
